import { Setter } from "../services/ComService"
import { parseStringFromServer } from "../services/TextConverter"
import { GLItemDTO, GLItemKind, GLItemStatus, IGLItem, weightedPotential } from "./GLItemDTO"

export type SalesChartMode = 'company' | 'project'

export type GLItemChartRow = {
    id: number
    headline: string
    status: GLItemStatus
    startdate: string
    revenue_potential?: number
    revenue_probability?: number
    customerId: number
    customerName: string
}

export type SalesChartSeries = {
    key: string
    label: string
    color: string
    itemId: number
    status: GLItemStatus
    customerName: string
    headline: string
}

export type SalesChartQuarter = {
    key: string
    year: number
    quarter: string
    label: string
}

export type SalesChartFilterOption = {
    id: string
    name: string
}

export type SalesChartPoint = {
    quarterKey: string
    year: number
    quarter: string
    label: string
    [seriesKey: string]: number | string
}

export type SalesChartStack = {
    points: SalesChartPoint[]
    series: SalesChartSeries[]
    quarters: SalesChartQuarter[]
    filterOptions: SalesChartFilterOption[]
}

const QUARTERS = ['Q1', 'Q2', 'Q3', 'Q4']

function colorFromKey(key: string): string {
    let hash = 0
    for (let i = 0; i < key.length; i++) {
        hash = key.charCodeAt(i) + ((hash << 5) - hash)
    }
    const hue = Math.abs(hash) % 360
    return `hsl(${hue}, 55%, 48%)`
}

function quarterIndex(quarter: string): number {
    const i = QUARTERS.indexOf(quarter)
    return i < 0 ? 0 : i
}

function quarterKey(year: number, quarter: string): string {
    return `${year}-${quarter}`
}

function hydrateChartRow(raw: any): GLItemChartRow {
    return {
        id: raw.id,
        headline: parseStringFromServer(raw.headline || ''),
        status: raw.status,
        startdate: raw.startdate || '',
        revenue_potential: raw.revenue_potential,
        revenue_probability: raw.revenue_probability,
        customerId: raw.customerId || 0,
        customerName: parseStringFromServer(raw.customerName || ''),
    }
}

export interface GLItemRequest {
    id?: number
    page?: number
    limit?: number
    personId?: number
    customerId?: number
    needle?: string
    kind?: GLItemKind
    status?: GLItemStatus
    userId?: number
    pastduedate?: boolean
    newsworthy?: boolean
}
export class GLItemsDTO {
    request: GLItemRequest
    items: GLItemDTO[] = []
    allowInfinitScroll: boolean = true
    total: number = 0
    statusCounts: Record<string, number> = {}
    statusCountsAll: number = 0
    chartItems: GLItemChartRow[] = []

    constructor(data: GLItemRequest) {
        this.request = data
    }

    patchRequest(data: GLItemRequest) {
        this.request = {...this.request, ...data}
    }

    async getStatusCountsFromServer(data?: GLItemRequest) {
        if (data) this.patchRequest(data)
        const result = await Setter('spt/gl_items/status_counts', {
            kind: this.request.kind,
            userId: this.request.userId,
            pastduedate: this.request.pastduedate,
        }) as { counts?: Record<string, number>, all?: number }
        this.statusCounts = result.counts || {}
        this.statusCountsAll = result.all || 0
        return result
    }

    countForStatus(status?: GLItemStatus) {
        if (status === undefined) {
            return this.statusCountsAll
        }
        return this.statusCounts[String(status)] || 0
    }

    async getFromServer(data?: GLItemRequest) {
        this.allowInfinitScroll = false
        if(data) this.patchRequest(data)
        const result = await Setter('spt/gl_items/search', this.request)
        const newItems = result.data.map((item: any) => new GLItemDTO(item))
        this.total = result.total
        if (newItems.length === 0) {
            return
        }
        this.allowInfinitScroll = true
        if (this.request.page === 1) {
            this.items = newItems
        } else {
            this.items = [...this.items, ...newItems]
        }
    }

    async getNextPage() {
        if (!this.allowInfinitScroll) { return }
        await this.getFromServer({page: (this.request.page || 1) + 1})
    }

    async getFresh() {
        this.items = []
        this.allowInfinitScroll = true
        await this.getFromServer({page: 1})
    }

    /** Replace-mode page load (for classic pagination, not infinite scroll). */
    async getPage(page: number) {
        this.allowInfinitScroll = false
        this.request.page = page
        const result = await Setter('spt/gl_items/search', this.request)
        this.items = result.data.map((item: any) => new GLItemDTO(item))
        this.total = result.total
    }

    async getChartFromServer(data?: { kind?: GLItemKind, status?: GLItemStatus }) {
        const payload: { kind?: GLItemKind, status?: GLItemStatus } = {
            kind: data?.kind !== undefined ? data.kind : this.request.kind,
        }
        if (data?.status !== undefined) {
            payload.status = data.status
        }
        const result = await Setter('spt/gl_items/chart', payload) as { data?: any[] } | undefined
        this.chartItems = (result?.data || []).map(hydrateChartRow)
        return this.chartItems
    }

    chartRowsForDisplay(status?: GLItemStatus): GLItemChartRow[] {
        return this.chartItems.filter(row => {
            if (status !== undefined && row.status !== status) {
                return false
            }
            if (!row.startdate) {
                return false
            }
            const quarter = GLItemDTO.quarterFromStartdate(row.startdate)
            if (!quarter || quarter.quarter === 'none') {
                return false
            }
            return weightedPotential(row.revenue_potential, row.revenue_probability) > 0
        })
    }

    buildStackedChart(mode: SalesChartMode, status?: GLItemStatus, filterId?: string): SalesChartStack {
        const statusRows = this.chartRowsForDisplay(status)
        const rows = statusRows.filter(row => {
            if (!filterId) {
                return true
            }
            if (mode === 'company') {
                return String(row.customerId || 0) === filterId
            }
            return String(row.id) === filterId
        })

        const filterOptions = this.chartFilterOptions(mode, status)
        const quarters = this.chartQuarterRaster(statusRows)
        const seriesMap = new Map<string, SalesChartSeries>()
        const values = new Map<string, Map<string, number>>()
        const totals = new Map<string, number>()

        rows.forEach(row => {
            const q = GLItemDTO.quarterFromStartdate(row.startdate)
            if (!q) {
                return
            }
            const qKey = quarterKey(q.year, q.quarter)
            const weight = weightedPotential(row.revenue_potential, row.revenue_probability)
            const seriesKey = mode === 'company'
                ? `c-${row.customerId || 0}`
                : `p-${row.id}`
            const customerName = row.customerName || 'Ohne Firma'
            const headline = row.headline || '(ohne Titel)'
            const label = mode === 'company'
                ? customerName
                : `${customerName} – ${headline}`

            if (!seriesMap.has(seriesKey)) {
                seriesMap.set(seriesKey, {
                    key: seriesKey,
                    label,
                    color: colorFromKey(seriesKey),
                    itemId: row.id,
                    status: row.status,
                    customerName,
                    headline,
                })
            }

            if (!values.has(qKey)) {
                values.set(qKey, new Map())
            }
            const bucket = values.get(qKey)!
            bucket.set(seriesKey, (bucket.get(seriesKey) || 0) + weight)
            totals.set(seriesKey, (totals.get(seriesKey) || 0) + weight)
        })

        const series = Array.from(seriesMap.values()).sort((a, b) => {
            return (totals.get(b.key) || 0) - (totals.get(a.key) || 0)
        })

        const points: SalesChartPoint[] = quarters.map(q => {
            const point: SalesChartPoint = {
                quarterKey: q.key,
                year: q.year,
                quarter: q.quarter,
                label: q.label,
            }
            const bucket = values.get(q.key)
            series.forEach(s => {
                point[s.key] = bucket?.get(s.key) || 0
            })
            return point
        })

        return { points, series, quarters, filterOptions }
    }

    chartFilterOptions(mode: SalesChartMode, status?: GLItemStatus): SalesChartFilterOption[] {
        const rows = this.chartRowsForDisplay(status)
        const seen = new Map<string, string>()
        rows.forEach(row => {
            if (mode === 'company') {
                const id = String(row.customerId || 0)
                if (!seen.has(id)) {
                    seen.set(id, row.customerName || 'Ohne Firma')
                }
            } else {
                const id = String(row.id)
                if (!seen.has(id)) {
                    const company = row.customerName || 'Ohne Firma'
                    const headline = row.headline || '(ohne Titel)'
                    seen.set(id, `${company} – ${headline}`)
                }
            }
        })
        return Array.from(seen.entries())
            .map(([id, name]) => ({ id, name }))
            .sort((a, b) => a.name.localeCompare(b.name, 'de'))
    }

    private chartQuarterRaster(rows: GLItemChartRow[]): SalesChartQuarter[] {
        const now = new Date()
        let minYear = now.getFullYear()
        let minQ = 0
        let maxYear = now.getFullYear()
        let maxQ = 3
        let hasData = false

        rows.forEach(row => {
            const q = GLItemDTO.quarterFromStartdate(row.startdate)
            if (!q) {
                return
            }
            const qi = quarterIndex(q.quarter)
            if (!hasData) {
                minYear = q.year
                minQ = qi
                maxYear = q.year
                maxQ = qi
                hasData = true
                return
            }
            if (q.year < minYear || (q.year === minYear && qi < minQ)) {
                minYear = q.year
                minQ = qi
            }
            if (q.year > maxYear || (q.year === maxYear && qi > maxQ)) {
                maxYear = q.year
                maxQ = qi
            }
        })

        const quarters: SalesChartQuarter[] = []
        for (let year = minYear; year <= maxYear; year++) {
            QUARTERS.forEach((quarter, qi) => {
                if (year === minYear && qi < minQ) {
                    return
                }
                if (year === maxYear && qi > maxQ) {
                    return
                }
                quarters.push({
                    key: quarterKey(year, quarter),
                    year,
                    quarter,
                    label: quarter,
                })
            })
        }
        return quarters
    }

    async setNeedle(needle: string) {
        this.items = []
        this.request.needle = needle
        await this.getFresh()
    }

    async createNew(data: IGLItem) {
        const newItem = new GLItemDTO(data)
        await newItem.save()
        await this.getFresh()
    }

    async deleteItem(id: number) {
        const item = this.items.find(i => i.id === id)
        if(item) {
            await item.delete()
            await this.getFresh()
        }
    }

    get() {
        let out: GLItemDTO[] = []
        if (this.request.id && this.request.id > -1) {
            // Detail view - do not show main item, only children:
            this.items.forEach(item => {
                if (item.children) {
                    for (let i = item.children.length -1; i >= 0; i--) {
                        const c = item.children[i]
                        out.push(c)
                    }
                }
            })
        } else {
            out = this.items
        }
        return out
    }

    getHeadlineDetails() {
        const item = this.items[0]
        if (!item) { return {
            headline: '',
            personName: '',
            customerName: '',
        } }
        return {
            headline: item.headline,
            personName: `${item.person?.firstname || ''} ${item.person?.lastname || ''}`,
            customerName: `${item.person?.customerName || ''}`,
        }
    }

    getHeadline1() {
        const details = this.getHeadlineDetails()
        return details.headline
    }

    getHeadline2() {
        const details = this.getHeadlineDetails()
        if (details.personName && details.customerName) {
            return `${details.personName} (${details.customerName})`
        }
    }
}
